import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Pencil,
  Radio,
  RefreshCw,
  Trash,
  X,
} from "@tamagui/lucide-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  useHouseholdSensors,
  useLatestVersions,
  useSensors,
  useSensorFirmwareUpdate,
  useUserRole,
  getSensorPillState,
  getSignalBand,
  KNOWN_BOOTLOADERS,
  SENSOR_OTA_ACTIVE_STATUSES,
  Sensor,
  SensorPillState,
  SensorType,
  SignalBand,
} from "common";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import {
  YStack,
  XStack,
  Text,
  Spinner,
  Card,
  Separator,
  Button,
  Popover,
  View,
  useMedia,
} from "tamagui";
import { Table } from "tamagui-config";

import SensorFirmwareUpdateDialog, {
  getSensorOtaSku,
} from "./SensorFirmwareUpdateDialog";
import SensorFormModal, {
  FormValues as CreateSensorFormValues,
} from "./SensorFormModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { TableCards } from "../../components/TableCards";
import { useSensorType } from "../../hooks/useSensorType";
import { useShowToast } from "../../hooks/useShowToast";
import { formatTimeAgo } from "../../i18n/formatters/date";
import { iconLabel } from "../../utils/a11y";
import { formatDate, formatDateTimeSeconds } from "../../utils/formatDate";

// Theme + label per inclusion-lifecycle pill state (mirrors mobile's
// SensorStatusPill STYLE_MAP): pending_stale/pairing_failed read as errors,
// pairing/pending/paired_awaiting_data are neutral in-progress states.
const PILL_STATE_THEME: Record<SensorPillState, string> = {
  active: "green",
  // Scanned but never handed to the hub — unstarted install work, not a fault.
  draft: "gray",
  // Gray, matching mobile. A sensor that has gone quiet is a coverage gap and
  // a never-paired one is unfinished install work — neither is a hazard, and
  // red made a whole column of them read as a fleet emergency.
  inactive: "gray",
  maintenance: "orange",
  pending: "yellow",
  pending_stale: "red",
  pairing: "yellow",
  pairing_failed: "red",
  paired_awaiting_data: "yellow",
};

// Progress text for a firmware update the hub is running right now, or the
// failure that ended one. A success is deliberately NOT surfaced here: the
// version column already shows the new number with a green tick the moment the
// sensor reports it, and a second "Updated" badge beside it said the same
// thing twice. `undefined` means the column falls through to the tick.
const describeSensorOta = (
  sensor: Sensor,
  t: (key: string, options?: Record<string, unknown>) => string,
): { label: string; failed: boolean } | undefined => {
  const status = sensor.otaStatus;
  if (!status) return undefined;
  if (status === "failed") {
    return { label: t("sensors.otaProgress.failed"), failed: true };
  }
  if (!SENSOR_OTA_ACTIVE_STATUSES.includes(status)) return undefined;
  if (status === "queued") {
    return { label: t("sensors.otaProgress.queued"), failed: false };
  }
  if (status === "starting") {
    return { label: t("sensors.otaProgress.starting"), failed: false };
  }
  if (status === "installing") {
    return { label: t("sensors.otaProgress.installing"), failed: false };
  }
  const percent = Math.max(0, Math.min(100, sensor.otaProgress ?? 0));
  return { label: t("sensors.otaProgress.downloading", { percent }), failed: false };
};

// Sensor.rssi bands (common getSignalBand) -> status colours.
const SIGNAL_COLOR: Record<SignalBand, "$statusGood" | "$statusWarning" | "$statusCritical"> = {
  good: "$statusGood",
  fair: "$statusWarning",
  poor: "$statusCritical",
};

const SIGNAL_BARS: Record<SignalBand, number> = { good: 3, fair: 2, poor: 1 };

// Three-bar meter; unlit bars stay visible so one bar reads as "1 of 3".
const SignalBars = ({ band }: { band: SignalBand }) => (
  <XStack gap={2} alignItems="flex-end" height={14}>
    {[6, 10, 14].map((height, i) => (
      <View
        key={height}
        width={4}
        height={height}
        borderRadius={1}
        backgroundColor={i < SIGNAL_BARS[band] ? SIGNAL_COLOR[band] : "$gray6"}
      />
    ))}
  </XStack>
);

const PILL_STATE_LABEL_KEY: Record<SensorPillState, string> = {
  active: "sensors.statusValue.active",
  draft: "sensors.statusValue.draft",
  inactive: "sensors.statusValue.inactive",
  maintenance: "sensors.statusValue.maintenance",
  pending: "sensors.statusValue.pending",
  pending_stale: "sensors.statusValue.pendingStale",
  pairing: "sensors.statusValue.pairing",
  pairing_failed: "sensors.statusValue.pairingFailed",
  paired_awaiting_data: "sensors.statusValue.pairedAwaitingData",
};

const StatusIndicator = ({ state }: { state: SensorPillState }) => {
  const { t } = useTranslation();

  return (
    // Sizing mirrors BaseStations.tsx's StatusIndicator (10px dot, $2 gap,
    // default text size) so the two tables' pills match.
    <View
      flexDirection="row"
      alignItems="center"
      gap="$2"
      theme={PILL_STATE_THEME[state] as never}
      backgroundColor="$color6"
      borderRadius={1000_000_000}
      paddingHorizontal="$2"
      paddingVertical="$1"
      alignSelf="flex-start"
    >
      <View
        width={10}
        height={10}
        backgroundColor="$color10"
        borderRadius={1000}
      />
      <Text color="$color10" fontWeight="$4">
        {t(PILL_STATE_LABEL_KEY[state])}
      </Text>
    </View>
  );
};

// Permissions come from the page's single useUserRole(): a per-row call
// started every row at "no permissions" until its own fetch resolved.
const ActionView = ({
  sensor,
  canUpdate,
  canDelete,
  onSensorDelete,
  onSensorEdit,
  onSensorUpdateFirmware,
}: {
  sensor: Sensor;
  canUpdate: boolean;
  canDelete: boolean;
  onSensorDelete: (sensor: Sensor) => void;
  onSensorEdit: (sensor: Sensor) => void;
  onSensorUpdateFirmware: (sensor: Sensor) => void;
}) => {
  const { t } = useTranslation();
  // Only render OTA for sensor types proven over-the-air (leak only at launch).
  const otaEligible = getSensorOtaSku(sensor) !== null;

  return (
    <View flexDirection="row" gap="$2" onPress={(e) => e.stopPropagation()}>
      {otaEligible && (
        <Button
          disabled={!canUpdate}
          icon={<RefreshCw size="$1" />}
          {...iconLabel(t("a11y.updateFirmware"))}
          onPress={(e) => {
            e.stopPropagation();
            onSensorUpdateFirmware(sensor);
          }}
          disabledStyle={{
            cursor: "default",
            opacity: 0.5,
          }}
        ></Button>
      )}
      <Button
        disabled={!canUpdate}
        icon={<Pencil size="$1" />}
        {...iconLabel(t("a11y.edit"))}
        onPress={(e) => {
          e.stopPropagation();
          onSensorEdit(sensor);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
      <Button
        disabled={!canDelete}
        icon={<Trash size="$1" />}
        {...iconLabel(t("a11y.delete"))}
        onPress={(e) => {
          e.stopPropagation();
          onSensorDelete(sensor);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
    </View>
  );
};

const formatTimestamp = (timestamp: number) =>
  formatDateTimeSeconds(timestamp, "N/A");

const tick = (ok: boolean) =>
  ok ? (
    <Check size={12} color="$statusGood" />
  ) : (
    <X size={12} color="$statusCritical" />
  );

// The version cell opens the sensor's self-reported boot info (VERSION frame):
// SPI bootloader fingerprint and vendor keys. An all-zero fingerprint means the
// sensor couldn't read its bootloader. The amber triangle flags anything short
// of a known bootloader plus vendor keys, so a bad unit shows without a click.
const BootInfoPopover = ({
  sensor,
  children,
}: {
  sensor: Sensor;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { bootInfoReportedAt, bootloaderFingerprint, vendorKeyed } = sensor;
  const bootloaderName =
    bootloaderFingerprint &&
    Object.prototype.hasOwnProperty.call(KNOWN_BOOTLOADERS, bootloaderFingerprint)
      ? KNOWN_BOOTLOADERS[bootloaderFingerprint]
      : undefined;
  const notMeasured = bootloaderFingerprint === "0000000000000000";
  const needsAttention =
    !!bootInfoReportedAt && (!bootloaderName || !vendorKeyed);

  return (
    // The panel is portalled, but React still bubbles its presses (and the
    // dismiss overlay's) up here: stop them before they open the sensor's row.
    <View alignSelf="flex-start" onPress={(e) => e.stopPropagation()}>
      <Popover placement="bottom-start" allowFlip stayInFrame offset={8}>
        <Popover.Trigger asChild>
          <XStack
            gap="$1.5"
            alignItems="center"
            cursor="pointer"
            hoverStyle={{ opacity: 0.8 }}
          >
            {children}
            {needsAttention && (
              <View {...iconLabel(t("sensors.boot.attention"))}>
                <AlertTriangle size={12} color="$statusWarning" />
              </View>
            )}
          </XStack>
        </Popover.Trigger>
        <Popover.Content
          elevate
          bordered
          padding="$3"
          gap="$2"
          borderRadius="$4"
          alignItems="stretch"
        >
          {!bootInfoReportedAt ? (
            <Text color="$gray10">{t("sensors.boot.notReported")}</Text>
          ) : (
            <>
              <XStack
                gap="$4"
                alignItems="center"
                justifyContent="space-between"
              >
                <Text>{t("sensors.table.bootloader")}</Text>
                {notMeasured ? (
                  <Text color="$gray10">{t("sensors.boot.notMeasured")}</Text>
                ) : (
                  <XStack gap="$1.5" alignItems="center">
                    {tick(!!bootloaderName)}
                    <Text>
                      {bootloaderName ?? t("sensors.boot.unknownBootloader")}
                    </Text>
                  </XStack>
                )}
              </XStack>
              <XStack
                gap="$4"
                alignItems="center"
                justifyContent="space-between"
              >
                <Text>{t("sensors.table.vendorKeyed")}</Text>
                {tick(!!vendorKeyed)}
              </XStack>
              <Text fontSize="$2" color="$gray10">
                {t("sensors.boot.reported", {
                  date: formatTimestamp(bootInfoReportedAt),
                })}
              </Text>
            </>
          )}
        </Popover.Content>
      </Popover>
    </View>
  );
};

const Sensors: React.FC = () => {
  const { sm } = useMedia();
  const rowCounter = useRef(-1);
  const { t } = useTranslation();
  const { hubId } = useParams<{ hubId: string }>();
  const navigate = useNavigate();
  const {
    fetchSensorsByHub,
    addSensor,
    updateSensor,
    removeSensor,
    hub,
    loading,
    error,
  } = useSensors(hubId);
  // Admin view is per-hub by route, but a base station's linked gateways are at
  // the same property and their sensors are the operator's problem too — leaving
  // them on a separate row means correlating two lists while diagnosing one
  // house. Merge them in, and mark which hub each one physically belongs to.
  const {
    sensors,
    gateways,
    loading: householdLoading,
    refetch: refetchHousehold,
  } = useHouseholdSensors(hubId);

  // hubId -> display name, for the gateway badge. Only linked gateways appear
  // here; anything whose hubId is the route's hub renders no badge at all.
  const gatewayNameById = useMemo(
    () => new Map(gateways.map((gateway) => [gateway.id, gateway.name])),
    [gateways],
  );

  // Mutations still act on the primary hub's own list, so a refresh has to
  // re-run both reads or the table silently keeps the pre-mutation household.
  const refreshSensors = useCallback(() => {
    if (hubId) fetchSensorsByHub(hubId);
    refetchHousehold();
  }, [hubId, fetchSensorsByHub, refetchHousehold]);
  const { permissions } = useUserRole();
  const { translateSensorType } = useSensorType();
  const { triggerSensorFirmwareUpdate } = useSensorFirmwareUpdate();
  // Latest per-variant GBL versions from the CI-published markers in S3.
  // Drives the ✓/✗ indicator next to each sensor's running version. Best-effort
  // — `latest` stays null when the endpoint isn't reachable (no indicator).
  const { latest: latestVersions } = useLatestVersions();
  const { showSuccessToast, showErrorToast } = useShowToast();
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUpdateFirmwareDialogOpen, setUpdateFirmwareDialogOpen] =
    useState(false);
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  // Column sort for the table (Name / Location / Sensor Type headers toggle
  // asc → desc → off). Unsorted by default so the list keeps the API's order
  // until the operator asks for one.
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    if (!hubId) {
      navigate("/base-stations");
    }
  }, [hubId]);

  const handleSensorClick = (sensorId: string) => {
    navigate(`/base-stations/${hubId}/sensors/${sensorId}/events`);
  };

  // Stable (setters only) so the memoized columns below never hold a stale one.
  const onSensorDelete = useCallback((sensor: Sensor) => {
    setSelectedSensor(sensor);
    setDeleteDialogOpen(true);
  }, []);

  const onSensorEdit = useCallback((sensor: Sensor) => {
    setSelectedSensor(sensor);
    setCreateDialogOpen(true);
  }, []);

  const onSensorUpdateFirmware = useCallback((sensor: Sensor) => {
    setSelectedSensor(sensor);
    setUpdateFirmwareDialogOpen(true);
  }, []);

  const handleConfirmFirmwareUpdate = async (version: string, sku: string) => {
    if (!selectedSensor || !hubId) return;
    const sensor = selectedSensor;
    try {
      await triggerSensorFirmwareUpdate({
        thingName: hubId,
        sensorId: sensor.id,
        version,
        sku,
      });
      showSuccessToast(
        t("sensors.updateFirmware.success", {
          name: sensor.name || sensor.serialNumber,
          version,
        }),
      );
    } catch (err) {
      showErrorToast(
        t("sensors.updateFirmware.error", {
          message: err instanceof Error ? err.message : String(err),
        }),
      );
    }
    setSelectedSensor(null);
  };

  const onSensorAdd = () => {
    setSelectedSensor(null);
    setCreateDialogOpen(true);
  };

  const handleSensorSubmit = async (values: CreateSensorFormValues) => {
    const payload = {
      hubId: values.hubId,
      name: values.name,
      location: values.location,
      sublocation: values.sublocation,
      dsk: values.dsk,
      type: values.type,
      version: values.version,
      serialNumber: values.serialNumber,
      status: values.status,
    };
    if (selectedSensor) {
      await updateSensor({ id: selectedSensor.id, ...payload });
    } else {
      await addSensor(payload);
    }
    setSelectedSensor(null);
    // The table renders the household list, which these mutations don't touch
    // directly. The onSensorCreated/Updated subscriptions do cover it, but an
    // admin page shouldn't silently depend on a delivered event.
    refreshSensors();
  };

  const handleDeleteSensorSubmit = async (sensor: Sensor) => {
    await removeSensor(sensor.id);
    setSelectedSensor(null);
    refreshSensors();
  };

  const columnHelper = createColumnHelper<Sensor>();

  // Memoized: flexRender mounts each cell fn as a component, so fresh column
  // defs every render remounted every cell (ActionView included) and taps
  // landing mid-remount were lost. translateSensorType only varies with `t`.
  const columns = useMemo(() => [
    columnHelper.accessor("type", {
      header: () => t("sensors.table.type"),
      // Sort by the label the operator reads, not the enum, so the column
      // lands in the order its own text suggests ("Air Quality" before
      // "HVAC Air Handler", not AIR_QUALITY before HVAC_AIRHANDLER_MONITOR).
      sortingFn: (rowA, rowB) =>
        translateSensorType(rowA.original.type as SensorType).localeCompare(
          translateSensorType(rowB.original.type as SensorType),
        ),
      cell: (info) => {
        const sensor = info.row.original;
        const label = translateSensorType(info.getValue() as SensorType);
        // Anything not owned by the route's hub came in via a linked gateway.
        // Compare hubIds rather than checking membership in the gateway map —
        // that way an unnamed or not-yet-loaded gateway still gets flagged as
        // "not on this base station" instead of silently posing as local.
        const isFromGateway = !!hubId && sensor.hubId !== hubId;
        if (!isFromGateway) return <Text>{label}</Text>;
        return (
          <XStack alignItems="center" gap="$2" flexWrap="wrap">
            <Text>{label}</Text>
            <XStack
              alignItems="center"
              gap="$1"
              paddingHorizontal="$2"
              paddingVertical="$1"
              borderRadius="$2"
              backgroundColor="$blue4"
              borderWidth={1}
              borderColor="$blue8">
              <Radio size={12} color="$blue11" />
              <Text fontSize="$1" color="$blue11">
                {gatewayNameById.get(sensor.hubId) ?? t("sensors.viaGateway")}
              </Text>
            </XStack>
          </XStack>
        );
      },
    }),
    columnHelper.accessor("location", {
      header: () => t("sensors.table.location"),
      sortingFn: "text",
      cell: (info) => {
        const sublocation = info.row.original.sublocation;
        return (
          <YStack>
            <Text>{info.getValue()}</Text>
            {sublocation ? (
              <Text fontSize="$2" color="$gray10">
                {sublocation}
              </Text>
            ) : null}
          </YStack>
        );
      },
    }),
    // Who signed the install off, stamped by completeSensorInstall from the
    // installer's own login when they finished pairing. Blank means the job was
    // never finished — a sensor reporting data is not the same as an installer
    // saying it is installed, which is the whole point of the stamp.
    columnHelper.accessor("installCompletedByName", {
      header: () => t("sensors.table.installer"),
      sortingFn: "text",
      sortUndefined: "last",
      cell: (info) => {
        const name = info.getValue();
        if (!name) return <Text color="$gray10">—</Text>;
        const when = formatDate(info.row.original.installCompletedAt, "");
        return (
          <YStack>
            <Text>{name}</Text>
            {when ? (
              <Text fontSize="$2" color="$gray10">
                {when}
              </Text>
            ) : null}
          </YStack>
        );
      },
    }),
    columnHelper.accessor("serialNumber", {
      header: () => t("sensors.table.serialNumber"),
      enableSorting: false,
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.accessor("nodeId", {
      header: () => t("sensors.table.node"),
      // Z-Wave node id the base station assigned at inclusion. The hub fills
      // it in (inclusion ack, then every boot-time version report), so it is
      // display-only here; blank until the hub has reported it.
      sortingFn: "basic",
      sortUndefined: "last",
      cell: (info) => <Text>{info.getValue() ?? "—"}</Text>,
    }),
    columnHelper.accessor("version", {
      header: () => t("sensors.table.version"),
      enableSorting: false,
      // ✓ when the sensor's running version matches the latest GBL published
      // for its SKU, ✗ when behind. No indicator when we don't know the SKU
      // (not OTA-eligible), the latest marker isn't published yet, or the
      // sensor hasn't reported a version yet.
      cell: (info) => {
        const sensor = info.row.original;
        const current = info.getValue();
        // An update in flight outranks the up-to-date tick: the running
        // version is stale by definition while the transfer is going, so a red
        // ✗ there reads as a fault rather than as work in progress.
        const ota = describeSensorOta(sensor, t);
        if (ota) {
          return (
            <BootInfoPopover sensor={sensor}>
              <Text>{current || "—"}</Text>
              <Text color={ota.failed ? "$statusCritical" : "$blue10"} fontSize="$1">
                {ota.label}
              </Text>
            </BootInfoPopover>
          );
        }
        const sku = getSensorOtaSku(sensor);
        const latestVersion = sku ? latestVersions?.sensors?.[sku]?.version : null;
        const indicator =
          !current || !latestVersion ? null : current === latestVersion ? (
            <Check size={12} color="$statusGood" />
          ) : (
            <X size={12} color="$statusCritical" />
          );
        return (
          <BootInfoPopover sensor={sensor}>
            <Text>{current || "—"}</Text>
            {indicator}
          </BootInfoPopover>
        );
      },
    }),
    columnHelper.accessor("rssi", {
      header: () => t("sensors.table.signal"),
      // Base-station-side RSSI of the sensor's last frame, live through the
      // onSensorUpdated subscription (hub sends a 1-min median on >=6 dB / 5 min). Blank
      // until the hub has reported one.
      sortingFn: "basic",
      sortUndefined: "last",
      cell: (info) => {
        const rssi = info.getValue();
        if (rssi == null) return <Text>—</Text>;
        const band = getSignalBand(rssi);
        const updatedAt = info.row.original.rssiUpdatedAt;
        return (
          <YStack gap="$0.5">
            <XStack gap="$2" alignItems="center">
              <SignalBars band={band} />
              <Text color={SIGNAL_COLOR[band]} fontWeight="$6">
                {t(`sensors.signal.${band}`)}
              </Text>
            </XStack>
            <Text fontSize="$2">{t("sensors.signal.dbm", { dbm: rssi })}</Text>
            {updatedAt ? (
              <Text fontSize="$2" color="$gray10">
                {t("sensors.signal.updated", {
                  time: formatTimeAgo(new Date(updatedAt * 1000)),
                })}
              </Text>
            ) : null}
          </YStack>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: () => t("sensors.table.status"),
      enableSorting: false,
      cell: (info) => (
        <StatusIndicator state={getSensorPillState(info.row.original)} />
      ),
    }),
    columnHelper.accessor("lastReported", {
      header: () => t("sensors.table.lastReported"),
      enableSorting: false,
      cell: (info) => <Text>{formatTimestamp(info.getValue())}</Text>,
    }),
    columnHelper.display({
      id: "actions",
      header: () => t("sensors.table.action"),
      cell: ({ row }) => (
        <ActionView
          sensor={row.original}
          canUpdate={permissions.sensor.update}
          canDelete={permissions.sensor.delete}
          onSensorDelete={onSensorDelete}
          onSensorEdit={onSensorEdit}
          onSensorUpdateFirmware={onSensorUpdateFirmware}
        />
      ),
    }),
  ], [
    t,
    hubId,
    gatewayNameById,
    latestVersions,
    permissions.sensor.update,
    permissions.sensor.delete,
    onSensorDelete,
    onSensorEdit,
    onSensorUpdateFirmware,
  ]);

  const table = useReactTable({
    data: sensors,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Versions the Update dialog can offer for the selected sensor's SKU — the
  // same /latest-versions payload that drives the ✓/✗ column, so the two
  // never disagree about what is published.
  const selectedSku = selectedSensor ? getSensorOtaSku(selectedSensor) : null;
  const selectedVariant = selectedSku
    ? latestVersions?.sensors?.[selectedSku]
    : undefined;

  const renderContent = () => {
    if (loading || householdLoading) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center" space="$4">
          <Spinner size="large" />
          <Text>{t("sensors.loading")}</Text>
        </YStack>
      );
    }

    if (error) {
      return (
        <YStack
          flex={1}
          justifyContent="center"
          alignItems="center"
          padding="$4"
        >
          <AlertTriangle size={48} color="$red10" />
          <Text marginTop="$4" color="$red10">
            {error?.message || t("common.error")}
          </Text>
          <Button
            marginTop="$4"
            onPress={refreshSensors}
          >
            {t("common.retry")}
          </Button>
        </YStack>
      );
    }

    if (!sensors || sensors.length === 0) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text>{t("sensors.noSensors")}</Text>
          <Button
            marginTop="$4"
            onPress={refreshSensors}
          >
            {t("common.refresh")}
          </Button>
        </YStack>
      );
    }

    const headerGroups = table.getHeaderGroups();
    const tableRows = table.getRowModel().rows;
    const footerGroups = table.getFooterGroups();

    const allRowsLength =
      tableRows.length + headerGroups.length + footerGroups.length;
    rowCounter.current = -1;

    if (sm) {
      // Same columns and row model as the desktop table, so a sort chosen on
      // a wide window survives a resize into the card layout. ActionView
      // stops propagation, so its buttons don't also open the sensor.
      return (
        <TableCards
          table={table}
          titleColumn="type"
          onRowPress={(row) => handleSensorClick(row.original.id)}
        />
      );
    }

    // Every column takes an equal share of the row; the floor per column is
    // the widest thing it must show on one line (the date, three action
    // buttons, the "Serial Number" header). Flex cells default to
    // min-width:auto, which is what let the action buttons spill past the card.
    const MIN_WIDTHS: Record<string, number> = {
      serialNumber: 150,
      nodeId: 90,
      version: 130,
      status: 130,
      lastReported: 220,
      actions: 220,
    };
    const cellSizing = (id: string) => ({
      flex: 1,
      minWidth: MIN_WIDTHS[id] ?? 150,
    });
    const minTableWidth = table
      .getVisibleLeafColumns()
      .reduce((sum, column) => sum + cellSizing(column.id).minWidth, 0);

    // Scrolls sideways only, below the column floors. Every row renders and
    // the page scrolls, same as Base Stations — no height cap.
    return (
      <YStack className="scroll-x-auto">
        <YStack minWidth={minTableWidth}>
          <Table borderWidth={0} maxWidth="100%" gap="$3">
            <Table.Head>
              {headerGroups.map((headerGroup) => {
                rowCounter.current++;
                return (
                  <Table.Row
                    rowLocation={
                      rowCounter.current === 0
                        ? "first"
                        : rowCounter.current === allRowsLength - 1
                          ? "last"
                          : "middle"
                    }
                    key={headerGroup.id}
                  >
                    {headerGroup.headers.map((header) => {
                      const label = header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          );
                      if (!header.column.getCanSort()) {
                        return (
                          <Table.HeaderCell
                            key={header.id}
                            borderWidth={0}
                            {...cellSizing(header.column.id)}
                          >
                            <Text fontWeight="bold">{label}</Text>
                          </Table.HeaderCell>
                        );
                      }
                      // Sortable header: click cycles asc → desc → unsorted.
                      // The idle arrows stay faint so the eye lands on the
                      // active direction, not on every sortable column.
                      const sorted = header.column.getIsSorted();
                      return (
                        <Table.HeaderCell
                          key={header.id}
                          borderWidth={0}
                          {...cellSizing(header.column.id)}
                        >
                          <XStack
                            alignItems="center"
                            gap="$1.5"
                            cursor="pointer"
                            hoverStyle={{ opacity: 0.8 }}
                            onPress={header.column.getToggleSortingHandler()}
                          >
                            <Text fontWeight="bold">{label}</Text>
                            {sorted === "asc" ? (
                              <ArrowUp size={14} />
                            ) : sorted === "desc" ? (
                              <ArrowDown size={14} />
                            ) : (
                              <ArrowUpDown size={14} color="$gray9" />
                            )}
                          </XStack>
                        </Table.HeaderCell>
                      );
                    })}
                  </Table.Row>
                );
              })}
            </Table.Head>
          </Table>
          <Table
            cellWidth="$20"
            cellHeight="$8"
            borderWidth={0}
            maxWidth="100%"
            gap="$3"
          >
            <Table.Body>
              {tableRows.map((row) => {
                rowCounter.current++;
                return (
                  <Table.Row
                    hoverStyle={{
                      backgroundColor: "$color2",
                      cursor: "pointer",
                    }}
                    pressStyle={{
                      backgroundColor: "$color3",
                    }}
                    onPress={() => handleSensorClick(row.original.id)}
                    rowLocation={
                      rowCounter.current === 0
                        ? "first"
                        : rowCounter.current === allRowsLength - 1
                          ? "last"
                          : "middle"
                    }
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        borderWidth={0}
                        {...cellSizing(cell.column.id)}
                      >
                        {cell.column.id === "status" ? (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )
                        ) : (
                          <Text theme="alt1">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Text>
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </YStack>
      </YStack>
    );
  };

  if (!hubId) {
    return;
  }

  return (
    <YStack flex={1} padding="$4" $xs={{ paddingHorizontal: 0 }}>
      <XStack justifyContent="space-between" flexWrap="wrap" gap="$3">
        <XStack alignItems="center" space="$2" flexShrink={1}>
          <Button
            size="$3"
            chromeless
            onPress={() => navigate("/base-stations")}
            aria-label={t("a11y.back")}
            icon={<Text fontSize="$10">←</Text>}
          />
          <YStack>
            <Text fontSize="$9" fontWeight="bold">
              {t("sensors.title")}
            </Text>
            {hub && (
              <Text fontSize="$3" color="$gray11">
                {t("sensors.forHub", { hubName: hub.name || hub.serialNumber })}
              </Text>
            )}
          </YStack>
        </XStack>
        <Button
          onPress={onSensorAdd}
          backgroundColor="$hgPrimary"
          size={"$4"}
          disabled={!permissions.sensor.create}
        >
          {t("sensors.addSensor")}
        </Button>
      </XStack>
      <Separator marginTop="$4" />

      {gateways.length > 0 && (
        <XStack
          marginTop="$4"
          padding="$3"
          gap="$2"
          alignItems="center"
          borderRadius="$3"
          backgroundColor="$blue3"
          borderWidth={1}
          borderColor="$blue7">
          <Radio size={16} color="$blue11" />
          <Text fontSize="$2" color="$blue11" flex={1} flexWrap="wrap">
            {t("sensors.gatewayNotice")}
          </Text>
        </XStack>
      )}

      {/* No ScrollView wrapper here — Layout already scrolls the page. */}
      <Card
        flex={1}
        marginTop="$4"
        padding="$4"
        overflow="hidden"
        $xs={{ padding: "$3" }}
      >
        {renderContent()}
      </Card>

      <SensorFormModal
        open={isCreateDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleSensorSubmit}
        sensor={selectedSensor}
        hubId={hubId}
      ></SensorFormModal>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => handleDeleteSensorSubmit(selectedSensor as Sensor)}
        title={t("sensors.confirm.title")}
        description={t("sensors.confirm.description")}
      ></ConfirmDialog>

      <SensorFirmwareUpdateDialog
        open={isUpdateFirmwareDialogOpen}
        onOpenChange={setUpdateFirmwareDialogOpen}
        sensor={selectedSensor}
        availableVersions={selectedVariant?.versions ?? []}
        latestVersion={selectedVariant?.version ?? null}
        onConfirm={handleConfirmFirmwareUpdate}
      />
    </YStack>
  );
};

export default Sensors;
